import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, Download, Trash2, Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

export const AccountDataSection = () => {
  const { user, logout } = useAuth();
  const [busy, setBusy] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [password, setPassword] = useState("");
  const token = localStorage.getItem("auth_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  if (!user) return null;

  const openPortal = async () => {
    setBusy("portal");
    try {
      const res = await axios.post(`${API}/api/subscriptions/portal`,
        { origin_url: window.location.origin }, auth);
      window.location.href = res.data.portal_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Portail indisponible.");
    } finally { setBusy(null); }
  };

  const exportData = async () => {
    setBusy("export");
    try {
      const res = await axios.get(`${API}/api/gdpr/export`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "anatradex_mes_donnees.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé.");
    } catch { toast.error("Export impossible."); }
    finally { setBusy(null); }
  };

  const deleteAccount = async (e) => {
    e.preventDefault();
    setBusy("delete");
    try {
      await axios.post(`${API}/api/gdpr/delete-account`, { password }, auth);
      toast.success("Compte supprimé. Au revoir !");
      setTimeout(() => { logout(); window.location.href = "/"; }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Suppression impossible.");
      setBusy(null);
    }
  };

  return (
    <div className="mt-8 space-y-5" data-testid="account-data-section">
      <div className="panel p-5">
        <div className="panel-title text-[14px] flex items-center gap-2"><CreditCard size={15} /> Abonnement</div>
        <p className="text-xs text-white/50 mt-1 mb-3">
          Résiliez, changez de carte ou consultez vos factures via le portail sécurisé Stripe.
          Formule actuelle : <b className="text-white">{user.tier === "institutional" ? "Expert" : user.tier === "signals" ? "Pro" : user.tier === "api" ? "Broker API" : "Free"}</b>
        </p>
        <button onClick={openPortal} disabled={busy === "portal"} className="btn text-sm px-4 py-2" data-testid="manage-subscription-btn">
          {busy === "portal" ? <Loader2 size={13} className="animate-spin" /> : <CreditCard size={13} />} Gérer mon abonnement
        </button>
      </div>

      <div className="panel p-5">
        <div className="panel-title text-[14px] flex items-center gap-2"><Download size={15} /> Mes données (RGPD)</div>
        <p className="text-xs text-white/50 mt-1 mb-3">
          Téléchargez une copie complète de vos données : profil, trades importés, rapports, alertes, feedbacks (droit à la portabilité).
        </p>
        <button onClick={exportData} disabled={busy === "export"} className="btn text-sm px-4 py-2" data-testid="gdpr-export-btn">
          {busy === "export" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Exporter mes données (JSON)
        </button>
      </div>

      <div className="panel p-5 border border-down/25">
        <div className="panel-title text-[14px] flex items-center gap-2 text-down"><AlertTriangle size={15} /> Zone dangereuse</div>
        <p className="text-xs text-white/50 mt-1 mb-3">
          Suppression définitive du compte et de toutes vos données (droit à l'effacement).
          Les transactions de paiement sont anonymisées (obligation comptable). <b className="text-down">Irréversible.</b>
        </p>
        {!confirmOpen ? (
          <button onClick={() => setConfirmOpen(true)} className="btn text-sm px-4 py-2 border-down/40 text-down hover:bg-down/10" data-testid="gdpr-delete-btn">
            <Trash2 size={13} /> Supprimer mon compte
          </button>
        ) : (
          <form onSubmit={deleteAccount} className="flex flex-col sm:flex-row gap-2 max-w-md">
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                   className="field flex-1" placeholder="Confirmez votre mot de passe" data-testid="gdpr-delete-password-input" />
            <button type="submit" disabled={busy === "delete" || !password}
                    className="btn text-sm px-4 py-2 border-down/40 text-down hover:bg-down/10 disabled:opacity-40"
                    data-testid="gdpr-delete-confirm-btn">
              {busy === "delete" ? <Loader2 size={13} className="animate-spin" /> : "Supprimer définitivement"}
            </button>
            <button type="button" onClick={() => setConfirmOpen(false)} className="btn text-sm px-3 py-2" data-testid="gdpr-delete-cancel-btn">
              Annuler
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
